import { defaultLocale, isLocale, locales, type Locale } from '@/i18n/config';

/**
 * Route table.
 *
 * The path segments stay Uzbek in every language — one file tree, one set of
 * slugs, one canonical shape per page with three language variants hanging off
 * it. Translating the slugs as well would mean fifteen path mappings, fifteen
 * redirects from the old URLs, and a `hreflang` cluster that no longer shares
 * a spine; the language prefix already tells both the visitor and the crawler
 * what they are reading.
 */
export const routes = {
  home: '',
  services: 'xizmatlar',
  projects: 'loyihalar',
  partners: 'hamkorlar',
  company: 'kompaniya',
  contact: 'aloqa',
} as const;

export type RouteKey = keyof typeof routes;

/** Menu order — the nav, the footer and the sitemap all read from this. */
export const navOrder: readonly RouteKey[] = [
  'home',
  'services',
  'projects',
  'partners',
  'company',
  'contact',
];

/**
 * The localised URL for a route: `path('ru', 'services', 'erp')` →
 * `/ru/xizmatlar#erp`. Every internal link in the site goes through here, so
 * a locale can never leak out of the prefix it belongs to.
 */
export function path(lang: Locale, key: RouteKey, hash?: string): string {
  const segment = routes[key];
  const base = segment ? `/${lang}/${segment}` : `/${lang}`;
  return hash ? `${base}#${hash}` : base;
}

/**
 * Splits a pathname into its locale and the rest, e.g. `/ru/xizmatlar` →
 * `{ locale: 'ru', rest: '/xizmatlar' }`. Used by the language switcher to
 * point at the current page in another language, and by the proxy to decide
 * whether a request is already prefixed.
 */
export function splitLocale(pathname: string): {
  locale: Locale | null;
  rest: string;
} {
  const [, first = '', ...tail] = pathname.split('/');
  if (!isLocale(first)) return { locale: null, rest: pathname };
  const rest = tail.length > 0 ? `/${tail.join('/')}` : '';
  return { locale: first, rest };
}

/** The same page, in another language. Falls back to that language's home. */
export function swapLocale(pathname: string, next: Locale): string {
  const { rest } = splitLocale(pathname);
  return `/${next}${rest}`;
}

/**
 * Picks the best locale for an `Accept-Language` header.
 *
 * Hand-rolled rather than `negotiator` + `intl-localematcher`: those two are
 * ~30 KB of dependency to rank three options, and the whole of the matching
 * this site needs is "highest q-value whose language subtag we recognise".
 */
export function matchLocale(
  acceptLanguage: string | null,
  accepts: Record<Locale, readonly string[]>,
): Locale {
  if (!acceptLanguage) return defaultLocale;

  const ranked = acceptLanguage
    .split(',')
    .map((part) => {
      const [tag = '', ...params] = part.trim().split(';');
      const q = params
        .map((param) => param.trim())
        .find((param) => param.startsWith('q='));
      const quality = q ? Number.parseFloat(q.slice(2)) : 1;
      return {
        // `ru-RU` and `ru` both answer to `ru`; casing is not significant.
        subtag: tag.trim().toLowerCase().split('-')[0] ?? '',
        quality: Number.isFinite(quality) ? quality : 0,
      };
    })
    .filter((entry) => entry.subtag.length > 0 && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality);

  for (const { subtag } of ranked) {
    const hit = locales.find((locale) => accepts[locale].includes(subtag));
    if (hit) return hit;
  }

  return defaultLocale;
}
